import React from 'react';
import StationAvatar from './StationAvatar';

/* ─── Per-scenario content ─────────────────────────────── */
const SCENARIOS = {
  dead: {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3.5 18.5L9 13l4 4L20 7"/><circle cx="12" cy="12" r="10"/>
        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
      </svg>
    ),
    accent: '#f59e0b',
    title: 'Station is off the air',
    message: "This station doesn't seem to be streaming right now — it might be on a break or having technical issues. Try again in a few minutes, or explore a similar station below.",
    retryLabel: 'Try Again',
    showSimilar: true,
  },
  dropped: {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 6l5 5a7 7 0 0 1 12 0l5-5"/><path d="M5 10l5 5a3 3 0 0 1 4 0l5-5"/>
        <line x1="12" y1="20" x2="12.01" y2="20"/>
        <line x1="2" y1="2" x2="22" y2="22"/>
      </svg>
    ),
    accent: '#ef4444',
    title: 'Stream connection lost',
    message: "The live stream dropped unexpectedly. This can happen due to network hiccups or the station briefly going offline. We tried reconnecting once automatically — you can try again or switch to a similar station.",
    retryLabel: 'Reconnect',
    showSimilar: true,
  },
  stalled: {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    accent: '#3b82f6',
    title: 'Stream is taking too long',
    message: "The audio has been buffering for a while. Your connection might be slow, or the station's server is busy. You can keep waiting or try a similar station in the meantime.",
    retryLabel: 'Keep Trying',
    showSimilar: true,
  },
  blocked: {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <polygon points="10 8 16 12 10 16 10 8"/>
      </svg>
    ),
    accent: 'var(--accent)',
    title: 'One tap to start playing',
    message: "Your browser requires a direct tap to start audio — this is a security requirement for all web apps. Just tap the button below to begin listening.",
    retryLabel: 'Tap to Play',
    showSimilar: false,
  },
  offline: {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="1" y1="1" x2="23" y2="23"/>
        <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/>
        <path d="M10.71 5.05A16 16 0 0 1 22.56 9"/><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/>
      </svg>
    ),
    accent: '#6b7280',
    title: 'You appear to be offline',
    message: "There's no internet connection right now. Radio streaming requires a live connection. Check your Wi-Fi or mobile data and try again when you're back online.",
    retryLabel: 'Try Again',
    showSimilar: false,
  },
};

/* ─── Component ─────────────────────────────────────────── */
const StreamErrorModal = ({ error, onDismiss, onRetry, onPlayStation }) => {
  if (!error) return null;

  const scenario = SCENARIOS[error.type] || SCENARIOS.dead;
  const { icon, accent, title, message, retryLabel, showSimilar } = scenario;
  const similar = showSimilar ? (error.similarStations || []) : [];

  return (
    <div className="modal-overlay sem-overlay" onClick={onDismiss}>
      <div className="modal-content sem-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="sem-header" style={{ '--sem-accent': accent }}>
          <div className="sem-icon-wrap">{icon}</div>
          <div className="sem-header-text">
            <p className="sem-station-name">{error.stationName}</p>
            <h2 className="sem-title">{title}</h2>
          </div>
          <button className="modal-close sem-close" onClick={onDismiss} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="sem-body">
          <p className="sem-message">{message}</p>

          {/* Primary actions */}
          <div className="sem-actions">
            <button className="sem-btn sem-btn-primary" onClick={onRetry}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/>
              </svg>
              {retryLabel}
            </button>
            <button className="sem-btn sem-btn-ghost" onClick={onDismiss}>
              Maybe Later
            </button>
          </div>

          {/* Similar stations */}
          {similar.length > 0 && (
            <div className="sem-similar">
              <p className="sem-similar-label">Similar stations you might enjoy</p>
              <div className="sem-similar-grid">
                {similar.map((s) => (
                  <button
                    key={s.id || s.name}
                    className="sem-station-card"
                    onClick={() => { onDismiss(); onPlayStation(s); }}
                    title={s.name}
                  >
                    <StationAvatar
                      name={s.name}
                      favicon={s.favicon}
                      homepage={s.homepage}
                      size={40}
                    />
                    <div className="sem-station-info">
                      <span className="sem-station-name-sm">{s.name}</span>
                      <span className="sem-station-meta">
                        {s.country_name || s.country || 'Radio'}
                      </span>
                    </div>
                    <svg className="sem-play-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="5 3 19 12 5 21"/>
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamErrorModal;
